import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, DollarSign, Clock, Wallet } from 'lucide-react';
import { b2bApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';

export default function B2BDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // `b2bApi` is a stable module-level import; setData/setLoading are
  // stable React setters → empty dep array gives a callback that's
  // referentially stable for the whole component lifetime.
  const loadDashboard = useCallback(() => {
    b2bApi.dashboard()
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" /></div>;
  if (!data) return null;

  const { person, org, stats } = data;

  return (
    <div data-testid="b2b-dashboard" className="p-8">
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-light text-foreground">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {org?.companyName && <span className="font-medium text-foreground">{org.companyName}</span>}
          {person?.commissionRate != null && <span> &middot; Commissierate: {person.commissionRate}%</span>}
          {person?.discountRate != null && <span> &middot; Korting: {person.discountRate}%</span>}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Wallet className="w-5 h-5" />
            </div>
            <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Credit saldo</span>
          </div>
          <p data-testid="credit-balance" className="text-2xl font-semibold text-foreground">{formatCurrency(stats.creditBalance, 'nl')}</p>
        </div>

        <Link to="/b2b/commissions" className="bg-card border border-border rounded-2xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
            <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Open commissies</span>
          </div>
          <p data-testid="pending-commissions" className="text-2xl font-semibold text-foreground">{stats.pendingCommissions}</p>
          <p className="text-xs text-muted-foreground mt-1">{formatCurrency(stats.pendingAmount, 'nl')} in afwachting</p>
        </Link>

        <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 text-green-700 flex items-center justify-center">
              <DollarSign className="w-5 h-5" />
            </div>
            <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Uitbetaald</span>
          </div>
          <p data-testid="paid-amount" className="text-2xl font-semibold text-foreground">{formatCurrency(stats.paidAmount, 'nl')}</p>
        </div>

        <Link to="/b2b/referrals" className="bg-card border border-border rounded-2xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <GitBranch className="w-5 h-5" />
            </div>
            <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Referrals</span>
          </div>
          <p data-testid="referral-count" className="text-2xl font-semibold text-foreground">{stats.referralCount}</p>
        </Link>
      </div>

      {/* Quick info */}
      {org && (
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          <h2 className="font-serif text-lg font-medium mb-4 text-foreground">Organisatie</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0 font-medium">Bedrijf</span><span className="text-foreground">{org.companyName}</span></div>
            <div className="flex gap-3"><span className="text-muted-foreground w-28 shrink-0 font-medium">BTW-nummer</span><span className="text-foreground">{org.vatNumber || '--'}</span></div>
            {org.addressStreet && (
              <div className="flex gap-3 col-span-full"><span className="text-muted-foreground w-28 shrink-0 font-medium">Adres</span>
                <span className="text-foreground">{[org.addressStreet, org.addressNumber].filter(Boolean).join(' ')}, {org.addressPostalCode} {org.addressCity}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
